import React, { useRef, useState, useEffect } from 'react';
import './Footer.css';
import logoImg from '../logo.png';


const RevealText = ({ children, delay = "0s", className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    const { current } = domRef;
    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, []);

  return (
    <div className={`line-wrapper ${className}`} ref={domRef}>
      <span 
        className={`reveal-text ${isVisible ? 'visible' : ''}`}
        style={{ transitionDelay: delay }}
      >
        {children}
      </span>
    </div>
  );
};

const Footer = () => {
  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-column brand-col">
          <div className="footer-logo">
            <RevealText><img src={logoImg} className="footer-logo-img" alt="Cabptoid Logo" /></RevealText>
            <div className="logo-text-group">
              <RevealText delay="0.1s"><span className="logo-main">Cabptoid</span></RevealText>
              <RevealText delay="0.2s"><span className="logo-sub">SOLUTIONS</span></RevealText>
            </div>
          </div>
          <div className="social-links">
            {['fb', 'tw', 'li', 'yu'].map((s, i) => (
              <RevealText key={s} delay={`${0.3 + i * 0.1}s`}>
                <a 
                  href={s === 'li' ? "https://www.linkedin.com/in/vinushnalakkakula/" : "/#"} 
                  className="social-link"
                  target={s === 'li' ? "_blank" : "_self"}
                  rel={s === 'li' ? "noopener noreferrer" : ""}
                >
                  {/* ... SVG icon placeholder ... */}
                  {s === 'fb' && <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>}
                  {s === 'tw' && <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.84 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>}
                  {s === 'li' && <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451c.981 0 1.771-.773 1.771-1.729V1.729C24 .774 23.207 0 22.225 0z"/></svg>}
                  {s === 'yu' && <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>}
                </a>
              </RevealText>
            ))}
          </div>
        </div>

        <div className="footer-column services-col">
          <RevealText><h3>Services</h3></RevealText>
          <ul className="footer-services-list">
            {[
              "Mobile App Development",
              "Web App Development",
              "Native App Development",
              "React JS Development"
            ].map((service, idx) => (
              <RevealText key={service} delay={`${0.1 + idx * 0.1}s`}>
                <li><a href="/#">{service}</a></li>
              </RevealText>
            ))}
          </ul>
        </div>

        <div className="footer-column contact-us-col">
          <RevealText><h3>Contact Us</h3></RevealText>
          <div className="contact-details new-contact-cards">
            <RevealText delay="0.1s">
              <div className="new-contact-row">
                <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                <span>Point of Contact: Vinushna Lakkakula</span>
              </div>
            </RevealText>
            
            <RevealText delay="0.2s">
              <div className="new-contact-row">
                <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                <span>+91 63047 65654</span>
              </div>
            </RevealText>
            
            <RevealText delay="0.3s">
              <div className="new-contact-row">
                <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                <span>vinushna.lakkakula@cabptoidsolutions.com</span>
              </div>
            </RevealText>
            
            <RevealText delay="0.4s">
              <div className="new-contact-row address-row">
                <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                <span>2nd Floor, Aikya Vihar, Kavuri Hills, Hyderabad,<br/>Telangana – 500033</span>
              </div>
            </RevealText>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="footer-bottom-content">
          <RevealText><p>©Cabptoid 2025. All rights reserved.</p></RevealText>
          <RevealText delay="0.2s"><a href="/#" className="privacy-link">Privacy Policy</a></RevealText>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
